import { useMutation, useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useDispatch } from 'react-redux';
import { useReviewCreationData, setShareResults, useShareResults } from 'reducers/reviewCreationSlice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useReviewCreationNavigation } from '../useReviewCreationNavigation';

export interface ShareResult {
  feedbackFormId: number;
  cohort: Cohort;
  shareResultsSettings: ShareResultsSettings;
  enabledProviderTypes: string[];
  providerTypeToTemplateMap: ProviderTypeToTemplateMap[];
  settingSameAsFormId: number;
  isAllowSameSettingAsFirstView: boolean;
  feedbackFormName: string;
}

export interface Cohort {
  id: number;
  name: string;
}

export interface ShareResultsSettings {
  isManagerPublishAllowed: boolean;
  isShowManagerRating: boolean;
  isAnonymizePeers: boolean;
  isShowManagerAnswers: null;
  isShowPeerAnswers: null;
  isShowUpwardAnswers: null;
}

export interface ProviderTypeToTemplateMap {
  template?: Template;
  providerType: string;
}

export interface Template {
  id: number;
  created: string;
  name: string;
  formType: string;
  createdByEmployeeId: number;
  isArchived: boolean;
  questionCount: number;
  templateSetting: TemplateSetting;
  createdByEmployee: object;
  isSample: boolean;
  ongoingReviewCycleCount: number;
  // createdByEmployeeMiniProfile: any;
}

export interface TemplateSetting {
  id: number;
  templateId: number;
  overallRatingDistribution: OverallRatingDistribution;
  isEnableGoalLevelQuestion: boolean;
  carryForwardAgenda: boolean;
}

export interface OverallRatingDistribution {
  PERFORMANCE: number;
  POTENTIAL: number;
  QNA: number;
}

const getShareResults = ({
  reviewCycleId,
}: {
  reviewCycleId: string;
}): Promise<{ entity: { shareResultsSettingsList: ShareResult[] } }> => {
  const url = endpoints.reviews.reviewCycleCreation.shareResults;
  return sendRequest({
    url,
    credentials: 'include',
    body: undefined,
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
  });
};

const putShareResults = ({
  reviewCycleId,
  shareResultsSettingsList,
}: {
  reviewCycleId: string;
  shareResultsSettingsList: ShareResult[];
}) => {
  const url = endpoints.reviews.reviewCycleCreation.shareResults;
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
    body: {
      shareResultsSettingsList,
    },
  });
};

export const useGetShareResults = ({ reviewCycleId }: { reviewCycleId: string }) => {
  const dispatch = useDispatch();
  return useQuery(
    [REACT_QUERY_KEYS.reviewCreation.SHARE_RESULTS, reviewCycleId],
    () =>
      getShareResults({ reviewCycleId }).then((res) => {
        const { shareResultsSettingsList } = res.entity;
        dispatch(setShareResults(shareResultsSettingsList));
        return shareResultsSettingsList;
      }),
    { enabled: !!reviewCycleId }
  );
};

export const useSaveShareResults = ({ isPreviewClick = false } = {}) => {
  const { goToNextStep, goToStep } = useReviewCreationNavigation();
  const shareResults: ShareResult[] = useShareResults();
  const { reviewCycleId } = useReviewCreationData();

  // TODO: v1 or later
  // const settingsForFeedbackFormIdsMap = new Map<number, ShareResult>();
  // shareResults.forEach((setting) => {
  //   settingsForFeedbackFormIdsMap.set(setting.feedbackFormId, setting);
  // });

  // const copiedSettingsFromViewIds = shareResults.map((setting) => {
  //   if (
  //     !setting.isAllowSameSettingAsFirstView ||
  //     setting.settingSameAsFormId === null ||
  //     !settingsForFeedbackFormIdsMap.has(setting.settingSameAsFormId)
  //   ) {
  //     return setting;
  //   }

  //   const copiedSetting = settingsForFeedbackFormIdsMap.get(
  //     setting.settingSameAsFormId
  //   )?.shareResultsSettings;

  //   return { ...setting, shareResultsSettings: copiedSetting };
  // });

  return useMutation({
    mutationFn: () =>
      putShareResults({
        reviewCycleId,
        shareResultsSettingsList: shareResults,
      }),
    onSuccess: (_data, variables: { nextStep?: string }) => {
      if (variables?.nextStep) {
        goToStep(variables.nextStep);
        return;
      }
      if (isPreviewClick) return;

      goToNextStep();
    },
    onError: () => {},
  });
};

export const useTemplateMetadata = ({
  reviewCycleId,
  providerType,
  feedbackFormId,
}: {
  reviewCycleId: string;
  providerType: string;
  feedbackFormId: number;
}) => {
  const { data: shareResults } = useGetShareResults({ reviewCycleId });

  if (!shareResults) return null;

  const metaDataForViewId = shareResults.find((f) => f.feedbackFormId === feedbackFormId);
  return metaDataForViewId?.providerTypeToTemplateMap.find((m) => m.providerType === providerType);
};
